// Дозапрос почтового индекса у Google, когда улица пришла из саджеста,
// а номер дома пользователь ввёл вручную.
//
// Чистые правила «надо ли дёргать Google» лежат в @/utils/postindexLookup —
// здесь только оркестрация: состояние запроса, вызов Places и запись индекса
// в профиль адреса.
//
// Композебл вызывается НЕ из setup() (CheckoutUnAuth.vue — Options API без
// setup), поэтому внутри нет useStore/хуков жизненного цикла: всё, что зависит
// от компонента, приходит аксессорами. Побочный эффект — composable легко
// покрывается юнит-тестами без монтирования компонента.
//
// Наружу отдаются только две точки входа: focusout по полю дома и сброс
// запомненного дома. Запрос в Google и разбор ответа — внутренние шаги.
import { choosePlace } from '@/utils/googlePlaces'
import { extractCourierAddressFromGoogle } from '@/utils/address'
import { checkMaxLength } from '@/utils/delivery'
import {
  isBuildField,
  shouldRequestPostindex,
  buildPostindexQuery,
} from '@/utils/postindexLookup'

/**
 * @param {object} ctx
 * @param {() => string} ctx.getCountryCode        код страны чекаута
 * @param {() => object} ctx.getProfile            addressInfo.PROFILE (мутируется — сюда пишем POSTINDEX)
 * @param {() => object} ctx.getFieldsConfig       fieldsConfig из /init
 * @param {() => object} ctx.getErrorMessages      объект ошибок полей для checkMaxLength
 * @param {() => boolean} ctx.isPickedFromSuggest  store.state.addressPickedFromSuggest
 * @param {(group: string, field: string, value: any) => void} ctx.validateField
 * @param {(key: string) => string} ctx.t          $t
 * @param {() => void} [ctx.onPostindexApplied]   индекс записан в профиль
 */
export function usePostindexLookup({
  getCountryCode,
  getProfile,
  getFieldsConfig,
  getErrorMessages,
  isPickedFromSuggest,
  validateField,
  t,
  onPostindexApplied = () => {},
}) {
  // Дом, для которого индекс уже запрашивали, — защита от повторного запроса
  // на каждый focusout без изменения значения.
  let lastLookupBuild = ''
  // Гонка ответов: пользователь успел поменять дом, пока Places отвечал.
  let requestId = 0

  function handleAddressFieldFocusOut(fieldName) {
    if (!isBuildField(fieldName)) return

    const needLookup = shouldRequestPostindex({
      countryCode: getCountryCode(),
      pickedFromSuggest: isPickedFromSuggest(),
      profile: getProfile(),
      lastLookupBuild,
    })

    if (!needLookup) return

    requestPostindexFromGoogle()
  }

  async function requestPostindexFromGoogle() {
    const profile = getProfile()
    const query = buildPostindexQuery(profile)
    if (!query) return

    lastLookupBuild = profile.BUILD
    const currentRequestId = ++requestId

    try {
      await choosePlace({
        inputValue: query,
        types: ['address'],
        country: getCountryCode(),
        onResult: (place) => {
          // Пользователь успел изменить дом — ответ уже неактуален
          if (currentRequestId !== requestId) return
          applyPostindexFromPlace(place)
        },
        options: { source: 'postindex_lookup' },
      })
    } catch (error) {
      console.warn('Не удалось получить индекс от Google:', error)
    }
  }

  function applyPostindexFromPlace(place) {
    if (!place) return

    const { postindex } = extractCourierAddressFromGoogle(
      place,
      getCountryCode()
    )

    if (!postindex) return

    const profile = getProfile()
    if (profile?.POSTINDEX) return

    profile.POSTINDEX = postindex
    validateField('ADDRESS_INFO_FIELDS', 'POSTINDEX', postindex)

    const postindexField = getFieldsConfig()?.ADDRESS_INFO_FIELDS?.find(
      (item) => item.name === 'POSTINDEX'
    )
    if (postindexField) {
      checkMaxLength(
        { target: { value: postindex } },
        postindexField,
        getErrorMessages(),
        t,
        profile
      )
    }

    onPostindexApplied()
  }

  // Адрес заново пришёл из саджеста — прошлый дом больше не актуален,
  // следующий focusout по BUILD снова имеет право сходить в Google.
  function resetLookupBuild() {
    lastLookupBuild = ''
  }

  return {
    handleAddressFieldFocusOut,
    resetLookupBuild,
  }
}
